import { DataTexture, DoubleSide, ShaderMaterial, Vector2, Vector3 } from 'three';
import { ImageDataTiledRenderer } from './ImageDataTiledRenderer';
import { ImageTypeConfigurationFactory } from './ImageTypeConfigurationFactory';
import { TileObject3D } from './tileGrid/Tile';
import { SEQUENTIAL_TILE_SHADER } from './SequentialTileShader';
import { Solids } from '@sick-davinci/glow';
/**
 * Create tiles with max available texture size and render given image-data into these tiles
 */
export class ImageDataSequentialTilesRenderer {
    constructor(_maxTextureSize) {
        this._maxTextureSize = _maxTextureSize;
        this._tiledRenderer = new ImageDataTiledRenderer({ width: _maxTextureSize, height: _maxTextureSize });
        this._imageTypeConfigurationFactory = new ImageTypeConfigurationFactory();
    }
    renderToObject3D(object, imageData, sceneIn) {
        /*
         * return early if no need for optimization
         */
        const DONT_DO_INFUTURE = true; // todo: use code below for bigger-than-texture-images (currently contains an unkown buggy, probably in the shader)
        if (DONT_DO_INFUTURE || imageData.Width <= this._maxTextureSize) {
            return this._tiledRenderer.renderToObject3D(object, imageData, sceneIn);
        }
        const TILE_WIDTH = imageData.Width;
        const TILE_HEIGHT = Math.floor(this._maxTextureSize * this._maxTextureSize / imageData.Width);
        /*
         * if image width is larger than _maxTextureSize^2 fall back to tiled renderer
         */
        if (TILE_HEIGHT <= 1) {
            return this._tiledRenderer.renderToObject3D(object, imageData, sceneIn);
        }
        const imageTypeConfiguration = this._imageTypeConfigurationFactory.createForType(imageData.ImageType);
        const imageRawData = this._createImageRawData(imageData);
        /*
         * create tiles
         */
        let imageHeightPassed = 0;
        const tileGrid = [];
        while (imageHeightPassed < imageData.Height) {
            const tileDimension = { width: TILE_WIDTH, height: TILE_HEIGHT };
            const textureDimension = { width: this._maxTextureSize, height: this._maxTextureSize };
            const imageHeightLeft = imageData.Height - imageHeightPassed;
            if (tileDimension.height > imageHeightLeft) {
                tileDimension.height = imageHeightLeft;
            }
            const start = imageHeightPassed * tileDimension.width * imageTypeConfiguration.stride;
            const end = start + textureDimension.height * textureDimension.width * imageTypeConfiguration.stride;
            const tileData = imageRawData.subarray(start, end);
            const textureDataLength = textureDimension.width * textureDimension.height * imageTypeConfiguration.stride;
            if (tileData.length < textureDataLength) {
                textureDimension.height = tileData.length / (textureDimension.width * imageTypeConfiguration.stride);
                textureDimension.height = Math.floor(textureDimension.height);
            }
            tileGrid.push({
                data: tileData,
                dimension: tileDimension,
                position: new Vector2(0, imageHeightPassed),
                gridPosition: new Vector2(0, tileGrid.length),
                textureDimension,
            });
            imageHeightPassed += tileDimension.height;
        }
        /*
         * render tiles
         */
        const existingTileObjects = object.children;
        existingTileObjects.forEach((tileObject) => {
            tileObject.material.uniforms.texture.value = null;
            tileObject.visible = false;
        });
        for (let tileIt = 0; tileIt < tileGrid.length; tileIt++) {
            const tile = tileGrid[tileIt];
            let tileObject = existingTileObjects[tileIt];
            if (!tileObject) {
                tileObject = this._createTileObject(tile);
                object.add(tileObject);
            }
            const texture = this._createTileObjectTexture(tile, imageData);
            texture.needsUpdate = true;
            tileObject.material.uniforms.texture.value = texture;
            tileObject.visible = true;
            tileObject.textureType = imageData.ImageType;
            const oldTileDimension = tileObject.geometry.parameters;
            const newTileDimension = tile.dimension;
            if (this._hasTileDimensionChanged(oldTileDimension, newTileDimension)) {
                tileObject.geometry.dispose();
                tileObject.geometry = this._createTileObjectGeometry(tile);
            }
            const position = this._calculateTileObjectPosition(tile, imageData);
            tileObject.position.set(position.x, position.y, position.z);
        }
    }
    _createImageRawData(imageData) {
        const { buffer, byteOffset, byteLength } = imageData.Pixels.data;
        return new Uint8Array(buffer, byteOffset, byteLength);
    }
    _getTextureFormat(imageType) {
        return this._imageTypeConfigurationFactory.createForType(imageType).textureFormat;
    }
    _createTileObjectTexture(tile, imageData) {
        return new DataTexture(tile.data, tile.textureDimension.width, tile.textureDimension.height, this._getTextureFormat(imageData.ImageType));
    }
    _createTileObjectShaderUniforms(tile, texture) {
        return {
            texture: { value: texture || null },
            textureDimension: { value: new Vector2(tile.textureDimension.width, tile.textureDimension.height) },
            tileDimension: { value: new Vector2(tile.dimension.width, tile.dimension.height) },
        };
    }
    _createTileObjectMaterial(uniforms) {
        return new ShaderMaterial({
            uniforms,
            vertexShader: SEQUENTIAL_TILE_SHADER.vertexShader,
            fragmentShader: SEQUENTIAL_TILE_SHADER.fragmentShader,
            depthWrite: false,
            side: DoubleSide,
        });
    }
    _createTileObjectGeometry(tile) {
        return Solids.createRectangle(tile.dimension.width, tile.dimension.height);
    }
    _createTileObject(tile) {
        const uniforms = this._createTileObjectShaderUniforms(tile);
        const material = this._createTileObjectMaterial(uniforms);
        const geometry = this._createTileObjectGeometry(tile);
        return new TileObject3D(geometry, material);
    }
    _hasTileDimensionChanged(oldDimension, newDimension) {
        return oldDimension.height !== newDimension.height || oldDimension.width !== newDimension.width;
    }
    _calculateTileObjectPosition(tile, imageData) {
        const xPosDiff = (imageData.Width - tile.dimension.width) / 2;
        const yPosDiff = (imageData.Height - tile.dimension.height) / 2;
        const xPos = tile.position.x - xPosDiff;
        const yPos = tile.position.y - yPosDiff;
        return new Vector3(xPos, yPos, 0);
    }
}
//# sourceMappingURL=ImageDataSequentialTilesRenderer.js.map